import type { CmmdDeviceRegistryRuntime, DesktopRelayHostState } from "@cmmd-forge/contracts";

export type ConnectionLifecyclePhase =
  | "connecting"
  | "online"
  | "recovering"
  | "offline"
  | "action-required";

export interface ConnectionLifecyclePresentation {
  readonly phase: ConnectionLifecyclePhase;
  readonly label: string;
  readonly detail: string | null;
  readonly tone: "success" | "warning" | "destructive" | "muted";
}

type ConnectionLifecycleSource =
  | { readonly kind: "remote-runtime"; readonly runtime: CmmdDeviceRegistryRuntime }
  | { readonly kind: "local-relay"; readonly state: DesktopRelayHostState };

function formatLastReachable(value: string | null): string | null {
  if (value === null) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `Last reachable ${date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function presentRemoteRuntime(runtime: CmmdDeviceRegistryRuntime): ConnectionLifecyclePresentation {
  const reachability = runtime.reachability ?? {
    status: runtime.status === "online" ? ("online" as const) : ("offline" as const),
    lastReachableAt: runtime.lastSeenAt,
  };
  const detail = formatLastReachable(reachability.lastReachableAt);
  if (reachability.status === "online") {
    return { phase: "online", label: "Available", detail, tone: "success" };
  }
  if (reachability.status === "suspect") {
    return { phase: "recovering", label: "Reconnecting", detail, tone: "warning" };
  }
  return { phase: "offline", label: "Offline", detail, tone: "muted" };
}

function presentLocalRelay(state: DesktopRelayHostState): ConnectionLifecyclePresentation {
  if (state.status === "connected") {
    return { phase: "online", label: "Available remotely", detail: null, tone: "success" };
  }
  if (state.status === "reconnecting") {
    return {
      phase: "recovering",
      label: "Reconnecting",
      detail: state.error ?? `Attempt ${state.attempt}`,
      tone: "warning",
    };
  }
  if (state.status === "connecting" || state.status === "authenticating") {
    return {
      phase: "connecting",
      label: state.status === "authenticating" ? "Authenticating" : "Connecting",
      detail: null,
      tone: "muted",
    };
  }
  if (state.status === "blocked") {
    return {
      phase: "action-required",
      label: "Action required",
      detail: state.error ?? null,
      tone: "destructive",
    };
  }
  return { phase: "offline", label: "Not connected", detail: null, tone: "muted" };
}

export function presentConnectionLifecycle(
  source: ConnectionLifecycleSource,
): ConnectionLifecyclePresentation {
  return source.kind === "remote-runtime"
    ? presentRemoteRuntime(source.runtime)
    : presentLocalRelay(source.state);
}
